import { useQuery } from "@tanstack/react-query";
import { Icon } from "components";
import { Shimmer } from "components/shimmer";
import QueryError from "components/query-error";
import { Card, CardAction, CardDescription, CardFooter, CardHeader, CardTitle } from "ui/components/card";
import { formatRelativeDate } from "utils/utils";
import { formatHoursToTime } from "utils/timesheet";
import { fetchBranchSummary } from "./api";

// Hours since the date, formatted like HoursCell (formatHoursToTime): "3h ago".
const hoursAgo = (date) => {
  const d = date ? new Date(date) : null;
  if (!d || Number.isNaN(d.getTime())) return "-";
  const hours = Math.max(0, (Date.now() - d.getTime()) / 3600000);
  return `${formatHoursToTime(hours, { hoursOnly: true })} ago`;
};

const GRID = "grid grid-cols-1 gap-4 px-4 mt-2.5 md:px-6 lg:grid-cols-2 xl:grid-cols-4";

// shadcn "section cards" layout: label + icon on top, big value, muted footer line. Semantic tokens only.
function StatCard({ label, icon, value, footer }) {
  return (
    <Card className="gap-4 py-5 shadow-none">
      <CardHeader className="px-5">
        <CardDescription className="font-medium">{label}</CardDescription>
        <CardAction>
          <span className="flex items-center justify-center rounded-md border bg-muted size-8 text-muted-foreground">
            <Icon icon={icon} className="size-4" />
          </span>
        </CardAction>
        <CardTitle className="text-2xl font-semibold tabular-nums truncate" title={String(value)}>
          {value}
        </CardTitle>
      </CardHeader>
      <CardFooter className="px-5 text-sm text-muted-foreground">
        <span className="truncate" title={footer}>
          {footer || "-"}
        </span>
      </CardFooter>
    </Card>
  );
}

function StatCardPlaceholder() {
  return (
    <Card className="gap-4 py-5 shadow-none">
      <CardHeader className="px-5">
        <Shimmer className="w-24 h-4" />
        <CardAction>
          <Shimmer className="size-8 rounded-md" />
        </CardAction>
        <Shimmer className="w-20 h-7" />
      </CardHeader>
      <CardFooter className="px-5">
        <Shimmer className="w-32 h-4" />
      </CardFooter>
    </Card>
  );
}

export default function RepoBranchStats({ repositoryUrl }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["integrations-github-branch-summary", repositoryUrl],
    queryFn: () => fetchBranchSummary({ repository_url: repositoryUrl }),
    enabled: !!repositoryUrl,
  });
  // Also shimmer while the repo (and so its URL) is still loading and the query is disabled.
  if (isLoading || !repositoryUrl) {
    return (
      <div className={GRID}>
        {[0, 1, 2, 3].map((i) => (
          <StatCardPlaceholder key={i} />
        ))}
      </div>
    );
  }
  if (isError) return <QueryError message="Failed to load branches." onRetry={refetch} />;
  const items = data?.items || [];
  const latest = [...items].sort((a, b) => new Date(b.last_commit?.date || 0) - new Date(a.last_commit?.date || 0))[0];
  const defaultBranch = items.find((b) => b.is_default);
  const committers = new Set(
    items.map((b) => b.last_commit?.author?.github_username || b.last_commit?.author?.name).filter(Boolean),
  ).size;
  return (
    <div className={GRID}>
      <StatCard
        label="Branches"
        icon="lucide:git-branch"
        value={data?.total ?? items.length}
        footer={`${items.filter((b) => b.is_protected).length} protected`}
      />
      <StatCard
        label="Default branch"
        icon="lucide:shield-check"
        value={defaultBranch?.name || "-"}
        footer={formatRelativeDate(defaultBranch?.last_commit?.date)}
      />
      <StatCard
        label="Last activity"
        icon="lucide:activity"
        value={hoursAgo(latest?.last_commit?.date)}
        footer={latest?.name}
      />
      <StatCard label="Recent committers" icon="lucide:users" value={committers} footer="Across branch heads" />
    </div>
  );
}
